/**
 * We aren't using the SDK on purpose because it would require us to
 * use an access key in production (all API calls need to be authenticated even
 * when calling open buckets).
 */

import Encoding from '../Typing/Encoding';
import DateParser from '../Typing/DateParser';
import { toBranchLabel } from '../../utils/utils';

export const LATEST_PACKAGE_FOLDER = 'latest';

const EXCLUDED_BRANCHES = new Set([
  'Content',
  'drop',
  'fail-over',
  'test',
]);

const EXCLUDED_VERSIONS = new Set([
  'src',
  'types',
]);

export interface PackageDetails {
  readonly timestamp?: Date;
  readonly dateLabel?: string;
  readonly label: string;
  readonly folder: string;
}

export const packageFactory = (id: string): PackageDetails => {
  return {
    folder: id,
    label: toBranchLabel(Encoding.decode(id)),
  };
}

export const latestPackage = (): PackageDetails => {
  return {
    folder: LATEST_PACKAGE_FOLDER,
    label: LATEST_PACKAGE_FOLDER,
    timestamp: new Date(),
    dateLabel: "Synced with changes",
  };
}

export class S3RequestError extends Error {
  public readonly status: number;
  public readonly url: string;

  constructor(status: number, url: string) {
    super(`S3 request failed with status ${status}: ${url}`);
    this.name = 'S3RequestError';
    this.status = status;
    this.url = url;
  }
}

export const isS3NotFoundError = (error: unknown): error is S3RequestError => {
  return error instanceof S3RequestError && error.status === 404;
};

interface ListObjectsOptions {
  readonly prefix?: string;
  readonly maxKeys?: number;
}

export class S3Client {
  private readonly bucket: string;
  private readonly subfolder: string;

  constructor(bucket: string, subfolder: string) {
    this.bucket = bucket;
    this.subfolder = subfolder;
  }

  public getCacheKey(): string {
    return `${this.bucket}/${this.subfolder}`;
  }

  public async getBranches(): Promise<PackageDetails[]> {
    const xml = await this.query({
      prefix: this.getS3Prefix(),
    });

    return this.convertObjectGroupList(xml)
        .filter(({ key, isFolder }) => isFolder && !key.startsWith('_') && !EXCLUDED_BRANCHES.has(key))
        .map(({ key }) => ({
          ...packageFactory(key),
          dateLabel: '',
          timestamp: new Date(),
        }));
  }

  public async getVersions(packageDetails: PackageDetails): Promise<PackageDetails[]> {
    const xml = await this.query({
      prefix: this.getS3Prefix(`${packageDetails.folder}/`),
    });
    const branchPrefix = `${packageDetails.folder}/`;

    const versionLabels = this.convertObjectGroupList(xml)
        .filter(({ key, isFolder }) => isFolder && !key.startsWith('_'))
        .map(({ key }) => key.startsWith(branchPrefix)
            ? key.slice(branchPrefix.length)
            : key)
        .filter(label => !EXCLUDED_VERSIONS.has(label));

    return versionLabels
        .map(label => {
          const timestamp = this.getDateFromLabel(label);
          return {
            label,
            timestamp,
            folder: label,
            dateLabel: DateParser.format(timestamp),
          };
        })
        .sort((a, b) => b.timestamp!.getTime() - a.timestamp!.getTime());
  }

  public async selectionExists(branch: string, version: string): Promise<boolean> {
    const xml = await this.query({
      prefix: this.getS3Prefix(`${branch}/${version}/`),
      maxKeys: 1,
    });
    const keyCount = xml.getElementsByTagName('KeyCount')[0]?.textContent ?? '0';
    return Number.parseInt(keyCount, 10) > 0;
  }

  private getS3Prefix(suffix = ''): string {
    return `${this.subfolder ? `${this.subfolder}/` : ''}${suffix}`;
  }

  private getDateFromLabel(version: string): Date {
    let timeUnit = parseInt(version, 10);
    if (timeUnit < 1000000000000) { // if it's unix seconds instead of js milliseconds
      timeUnit *= 1000;
    }
    return new Date(timeUnit);
  }

  private convertObjectGroupList(xml: Document): Array<{ key: string; isFolder: boolean }> {
    return Array
        .from(xml.getElementsByTagName('CommonPrefixes'))
        .map(el => {
          const rawKey = el.getElementsByTagName('Prefix')[0]?.textContent;
          if (!rawKey) {
            return null;
          }

          return {
            key: this.removeSubfolderPrefix(rawKey).replace(/\/$/, ''),
            isFolder: rawKey.endsWith('/'),
          };
        })
        .filter((entry): entry is { key: string; isFolder: boolean } => entry !== null);
  }

  private removeSubfolderPrefix(key: string): string {
    const prefix = this.subfolder ? `${this.subfolder}/` : '';
    return prefix && key.startsWith(prefix)
        ? key.slice(prefix.length)
        : key;
  }

  private async query(options: ListObjectsOptions): Promise<Document> {
    const searchParams = new URLSearchParams({
      'list-type': '2',
      delimiter: '/',
    });

    if (options.prefix) {
      searchParams.set('prefix', options.prefix);
    }
    if (options.maxKeys !== undefined) {
      searchParams.set('max-keys', `${options.maxKeys}`);
    }

    const url = new URL(`https://${this.bucket}.s3.amazonaws.com/`);
    url.search = searchParams.toString();
    const response = await fetch(url);
    if (!response.ok) {
      throw new S3RequestError(response.status, url.toString());
    }

    const text = await response.text();
    return (new DOMParser()).parseFromString(text, 'text/xml');
  }
}
